import React, { useState, useEffect } from 'react';
import { 
  Container, Grid, Typography, Box, TextField, Paper,
  createTheme, ThemeProvider, Snackbar, Alert, Card, CardMedia, CardContent
} from '@mui/material'; // Remove duplicate import of Typography
import { Newspaper, CalendarDays } from 'lucide-react';
import NewsCard from '../components/NewsCard';
import EventCard from '../components/EventCard';
import { apiService } from '../components/ReasuableFuncations/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    background: {
      default: '#f5f7fa'
    }
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff'
        }
      }
    }
  }
});

function NewsAndEventsPage() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;

  const fetchData = async () => {
    try {
      let newsUrl = '/news';
      let eventsUrl = '/events';
      
      // Add query parameters based on user type
      if (selectedOption === 'Regional Network' && regionalNetworkOption) {
        newsUrl += `?regionalNetwork=${encodeURIComponent(regionalNetworkOption)}`;
        eventsUrl += `?regionalNetwork=${encodeURIComponent(regionalNetworkOption)}`;
      } else if (selectedOption === 'Member State' && countryOption) {
        newsUrl += `?memberState=${encodeURIComponent(memberStateOption)}&country=${encodeURIComponent(countryOption)}`;
        eventsUrl += `?memberState=${encodeURIComponent(memberStateOption)}&country=${encodeURIComponent(countryOption)}`;
      }

      const newsResponse = await apiService.get(newsUrl);
      const eventsResponse = await apiService.get(eventsUrl);
      
      if (!newsResponse.data || !eventsResponse.data) {
        throw new Error('Failed to fetch data');
      }
      
      setNews(newsResponse.data);
      setEvents(eventsResponse.data);
      setFilteredEvents(eventsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      handleFetchError();
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (startDate || endDate) {
      const filtered = events.filter(event => {
        const eventDate = new Date(event.date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        return (!start || eventDate >= start) && (!end || eventDate <= end);
      });
      setFilteredEvents(filtered);
    } else {
      setFilteredEvents(events);
      console.log(events);

    
    }
  }, [startDate, endDate, events]);

  const handleFetchError = () => {
    setSnackbarMessage('Error fetching data. Please try again later.');
    setSnackbarSeverity('error');
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>

            {/* News Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Newspaper size={28} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                  Latest News
                </Typography>
              </Box>
              
              <Grid container spacing={1}>
                {news.map((item) => (
                  <Card sx={{marginBottom:2}}>
                  <CardMedia
                    component="img"
                    image={item.image.url}
                    alt={item.title}
                    sx={{
                      width: '100%',
                      height: 'auto',
                    }}
                  />
                  <CardContent>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: 'bold' }} 
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(item.date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body1">
                      {item.content}
                    </Typography>
                  </CardContent>
                </Card>
                ))}
              </Grid>
            </Grid>

            {/* Events Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <CalendarDays size={28} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                  Upcoming Events
                </Typography>
              </Box>

              <Paper sx={{ p: 3, mb: 4, bgcolor: 'white' }} elevation={0}>
                <Typography variant="h6" gutterBottom>
                  Filter Events
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Grid container spacing={2}>
                {filteredEvents.length > 0 ? (
                  filteredEvents.map((event) => (
                    <Grid item xs={12} key={event._id}>
                      <EventCard
                        title={event.title}
                        date={event.date}
                        content={event.content}
                        selectedOption={event.selectedOption}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0}>
                      <CalendarDays size={40} style={{ color: '#9CA3AF', marginBottom: 16 }} />
                      <Typography color="textSecondary">
                        No events found for the selected date range.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Container>

        <Snackbar
          open={Boolean(snackbarMessage)}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage('')}
        >
          <Alert
            onClose={() => setSnackbarMessage('')}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default NewsAndEventsPage;
